/**
 * Module: screens/onboarding/store
 * Role: Zustand wizard draft state for buyer onboarding (FTR-1115).
 *       Persisted to localStorage with 15-min TTL so browser refresh doesn't lose
 *       partially-filled steps. On success or explicit clear the store is reset.
 *       TTL resets on any user activity (setSlide/setCity/toggleMarketplace).
 *
 * Uses: zustand, zustand/middleware (persist), ./constants (MarketplaceCode)
 * Used by:
 *   - screens/onboarding/BuyerOnboardingScreen.tsx (root)
 *   - screens/onboarding/steps/WelcomeStep.tsx
 *   - screens/onboarding/steps/CityStep.tsx
 *   - screens/onboarding/steps/PreparingStep.tsx
 *
 * Glossary: ai/glossary/buyer.md
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { MarketplaceCode } from './constants';

export const DRAFT_TTL_MS = 15 * 60 * 1000; // 15 minutes
const STORAGE_KEY = 'awardy-buyer-onboarding-v1';

export interface OnboardingDraft {
  /** Current welcome slide index: 0 | 1 | 2 */
  slide: 0 | 1 | 2;
  /** Selected city (optional) */
  city: string | null;
  /** Selected marketplaces (optional, multi-select) */
  marketplaces: MarketplaceCode[];
  /** Timestamp for TTL check (ms since epoch); refreshed on any user action */
  createdAt: number;
}

interface OnboardingStore {
  slide: 0 | 1 | 2;
  city: string | null;
  marketplaces: MarketplaceCode[];
  createdAt: number;
  setSlide(slide: 0 | 1 | 2): void;
  setCity(city: string | null): void;
  toggleMarketplace(code: MarketplaceCode): void;
  resetDraft(): void;
}

function defaultState(): Omit<OnboardingStore, 'setSlide' | 'setCity' | 'toggleMarketplace' | 'resetDraft'> {
  return {
    slide: 0,
    city: null,
    marketplaces: [],
    createdAt: Date.now(),
  };
}

// ─── BUG-1165: deep-link stash (sessionStorage, not localStorage — TTL = session) ───
const PENDING_DEEPLINK_KEY = 'pending_deeplink';

/** Stash a deep-link target so it survives the onboarding redirect race. */
export function stashPendingDeeplink(path: string): void {
  try {
    sessionStorage.setItem(PENDING_DEEPLINK_KEY, path);
  } catch {
    // sessionStorage may be unavailable in some WebView edge cases — fail silently
  }
}

/** Read and remove the stashed deep-link (one-shot consume). Returns null if empty. */
export function consumePendingDeeplink(): string | null {
  try {
    const val = sessionStorage.getItem(PENDING_DEEPLINK_KEY);
    if (val) sessionStorage.removeItem(PENDING_DEEPLINK_KEY);
    return val;
  } catch {
    return null;
  }
}

export const useOnboardingStore = create<OnboardingStore>()(
  persist(
    (set) => ({
      ...defaultState(),
      setSlide(slide) {
        set({ slide, createdAt: Date.now() });
      },
      setCity(city) {
        set({ city, createdAt: Date.now() });
      },
      toggleMarketplace(code) {
        set((s) => {
          const already = s.marketplaces.includes(code);
          const marketplaces = already
            ? s.marketplaces.filter((m) => m !== code)
            : ([...s.marketplaces, code] as MarketplaceCode[]);
          return { marketplaces, createdAt: Date.now() };
        });
      },
      resetDraft() {
        set(defaultState());
      },
    }),
    {
      name: STORAGE_KEY,
      // Expire draft after 15 minutes of inactivity
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const age = Date.now() - (state.createdAt ?? 0);
        if (age > DRAFT_TTL_MS) {
          state.resetDraft();
        }
      },
    },
  ),
);
